import { Component, effect, inject, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { HasPermissionDirective } from "../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../services/api/permission/permission.service";
import { AssetHierarchyComponent } from "../../shared/widgets/asset-hierarchy/asset-hierarchy.component";
import { ThiefProtectionService } from "../../services/api/thief-protection/thief-protection.service";
import { ASSET_TYPES, AssetType } from "../../enums/assetTypes.enum";
import { first } from "rxjs";
import { ThiefProtectionAlert } from "../../models/api/thiefProtection";
import { createTimeoutSignal } from "../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../shared/components/timeout/timeout.component";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { TableLazyLoadEvent, TableModule } from "@openng/optimus-ui/table";
import { PAGINATOR_CONSTANTS } from "../../constants/paginator";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Column } from "../../models/ui/tableColumn";
import { NgClass } from "@angular/common";
import { DateTime } from "luxon";
import { Tooltip } from "@openng/optimus-ui/tooltip";
import { GateIdentifierPipe } from "../../shared/pipes/gateIdentifier/gate-identifier.pipe";
import { SvgIconComponent } from "../../shared/components/svg-icon/svg-icon.component";
import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { ResolveAlert } from "./resolve-alert/resolve-alert.component";
import { AuthService } from "../../services/api/auth/auth.service";
import { SelectButtonModule } from "@openng/optimus-ui/selectbutton";
import { DropdownOption } from "../../models/ui/dropdownOption";
import { FormsModule } from "@angular/forms";
import { MessageService } from "@openng/optimus-ui/api";

@Component({
  standalone: true,
  selector: "app-security-tool",
  imports: [
    FormsModule,
    TranslateModule,
    TableModule,
    HasPermissionDirective,
    AssetHierarchyComponent,
    TimeoutComponent,
    ProgressSpinnerModule,
    NgClass,
    Tooltip,
    SvgIconComponent,
    SelectButtonModule,
  ],
  templateUrl: "./security-tool.component.html",
  styleUrl: "./security-tool.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class SecurityToolComponent implements OnInit {
  public permissions = inject(PermissionService);
  public translate = inject(TranslateService);
  public gateIdentifierPipe = inject(GateIdentifierPipe);
  private thiefProtectionService = inject(ThiefProtectionService);
  private dialogService = inject(DialogService);
  private authService = inject(AuthService);
  private message = inject(MessageService);

  public paginatorOptions = PAGINATOR_CONSTANTS;

  public loading = signal(true);
  public timeOut = createTimeoutSignal(this.loading);
  public paginatedAlerts = signal<ThiefProtectionAlert[]>([]);
  public selectedAsset = signal<{ assetId: string; assetType: string } | null>(null);
  public totalRecords = signal(0);
  public first = signal(0);
  public rows = signal(this.paginatorOptions.DEFAULT_ROWS);
  private ref: DynamicDialogRef | null = null;

  public COLUMNS: Column[] = [
    {
      field: "gateSystem",
      header: "security_tool.table.gate_system",
    },
    {
      field: "alertType",
      header: "security_tool.table.alert_type",
    },
    {
      field: "severity",
      header: "security_tool.table.alert_severity",
    },
    {
      field: "triggeredAt",
      header: "security_tool.table.created_at",
    },
    {
      field: "resolvedAt",
      header: "security_tool.table.resolved_at",
    },
    {
      field: "resolutionNote",
      header: "security_tool.table.message",
    },
    {
      field: "isResolved",
      header: "security_tool.table.resolved_state",
    },
  ];

  public resolvedOptions: DropdownOption[] = [
    {
      value: false,
      label: "security_tool.table.resolved_false",
    },
    {
      value: true,
      label: "security_tool.table.resolved_true",
    },
  ];
  public resolved = signal(false);

  ngOnInit(): void {
    this.loading.set(true);
    this.getInitialData();
  }

  constructor() {
    effect(() => {
      const asset = this.selectedAsset();
      const resolved = this.resolved();
      if (asset) {
        this.first.set(0);
        this.getAlerts(asset.assetType, asset.assetId, resolved, 1, this.rows());
      }
    });
  }

  /**
   * Initially gets all alerts for the currently authenticated user
   */
  private getInitialData() {
    this.authService
      .getAuthenticatedUser()
      .pipe(first())
      .subscribe({
        next: (user) => {
          if (user) {
            if (user.customer) {
              const customerId = user.customer.id;
              this.selectedAsset.set({
                assetId: customerId!,
                assetType: ASSET_TYPES.CUSTOMER,
              });
            } else {
              this.message.add({
                key: "global",
                severity: "error",
                summary: this.translate.instant("common.error"),
                detail: this.translate.instant(`error_messages.FORBIDDEN`),
                life: 4500,
              });
            }
          }
        },
      });
  }

  /**
   * Gets either all resolved or active alerts for the specified asset
   * @param assetType The type of the asset for which the alerts are to be retrieved
   * @param assetId The id of the asset for which the alerts are to be retrieved
   * @param resolved A boolean that indicates whether active or resolved alerts are to be retrieved
   */
  private getAlerts(assetType: string, assetId: string, resolved: boolean, page: number, limit: number) {
    this.thiefProtectionService
      .getThiefProtectionAlerts(assetType as AssetType, assetId, resolved, page, limit)
      .pipe(first())
      .subscribe({
        next: (response) => {
          if (response) {
            this.paginatedAlerts.set(response.data);
            this.totalRecords.set(response.pagination.total);
            if (this.loading()) this.loading.set(false);
          }
        },
      });
  }

  /**
   * Handles asset click event.
   * Requests fault reporting data for the selected asset
   * and updates the table with the received data.
   * @param asset asset object with id and assetType properties
   */
  public onAssetClicked(asset: { id: string; assetType: string }) {
    this.selectedAsset.set({ assetId: asset.id, assetType: asset.assetType });
  }

  /**
   * Converts a timestamp in milliseconds to a formatted date string.
   * @param time - The timestamp in milliseconds.
   * @returns A string formated
   */
  public convertTime(time: string) {
    return DateTime.fromMillis(parseInt(time)).toFormat("dd.MM.yyyy - HH:mm:ss");
  }

  /**
   * Handles click on resolve button.
   * Opens dialog to resolve clicked alert
   */
  public onResolveClick(alert: ThiefProtectionAlert) {
    this.openResolveForm(alert);
  }

  /**
   * Opens the resolve dialog for the provided alert
   * @param alert The alert to be resolved
   */
  private openResolveForm(alert: ThiefProtectionAlert) {
    this.ref = this.dialogService.open(ResolveAlert, {
      header: this.translate.instant("security_tool.resolve_form.header"),
      modal: true,
      data: {
        alert,
      },
      width: "25vw",
      contentStyle: { overflow: "auto" },
    });
    this.ref?.onClose.subscribe((success) => {
      if (success) {
        const asset = this.selectedAsset();
        if (asset) {
          const currentPage = Math.floor(this.first() / this.rows()) + 1;
          this.getAlerts(asset.assetType, asset.assetId, this.resolved(), currentPage, this.rows());
        }
      }
    });
  }

  /**
   * Handles the table lazy load event and gets the events for the next page from the backend
   * @param event The table lazy load event emitted from the table
   */
  public onLazyLoad(event: TableLazyLoadEvent) {
    const limit = event.rows || this.paginatorOptions.DEFAULT_ROWS;
    const page = Math.floor((event.first || 0) / limit) + 1;

    this.first.set(event.first || 0);
    this.rows.set(limit);
    this.getAlerts(this.selectedAsset()!.assetType, this.selectedAsset()!.assetId, this.resolved(), page, limit);
  }
}
